import React, { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Ticket as TicketIcon,
  MapPin,
  Calendar,
  Clock,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Hourglass,
  Loader2,
} from 'lucide-react';

/**
 * /tickets/:token — Standalone, login-less ticket display.
 *
 * Security model:
 *  • The 48-char URL-safe `token` IS the bearer credential. The page renders
 *    only the SANITIZED payload returned by GET /api/v2/public/tickets/{token}.
 *  • Status badge logic relies ONLY on server-returned `booking_status`,
 *    `checked_in`, and `ticket_expires_at` — never local flags.
 *  • The QR encodes the server-signed JWT (`ticket_token`); admins validate
 *    server-side at scan.
 */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// ─── Status logic (derived ONLY from server fields) ────────────────────────
const deriveStatus = (data) => {
  if (!data) return { label: 'INVALID', tone: 'red' };

  const expiresAt = data.ticket_expires_at ? new Date(data.ticket_expires_at) : null;
  const now = new Date();

  if (data.booking_status === 'cancelled') return { label: 'CANCELLED', tone: 'red' };
  if (data.booking_status === 'refunded') return { label: 'REFUNDED', tone: 'red' };
  if (data.booking_status === 'partially_refunded') return { label: 'PARTIALLY REFUNDED', tone: 'amber' };
  if (expiresAt && expiresAt.getTime() < now.getTime()) return { label: 'EXPIRED', tone: 'red' };
  if (data.checked_in) return { label: 'ALREADY USED', tone: 'gray' };
  if (data.booking_status === 'confirmed' && data.payment_status === 'paid') {
    return { label: 'VALID', tone: 'green' };
  }
  return { label: 'PENDING', tone: 'amber' };
};

const statusToneClasses = {
  green: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/40',
  red: 'bg-red-500/15 text-red-300 border-red-400/40',
  gray: 'bg-zinc-500/15 text-zinc-300 border-zinc-400/40',
  amber: 'bg-amber-500/15 text-amber-300 border-amber-400/40',
};


// ─── Pretty formatters ─────────────────────────────────────────────────────
const formatEventDate = (yyyy_mm_dd) => {
  if (!yyyy_mm_dd) return '';
  try {
    const d = new Date(yyyy_mm_dd + 'T00:00:00');
    return d.toLocaleDateString('en-IN', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return yyyy_mm_dd;
  }
};

const formatTime = (hhmm) => {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map((x) => parseInt(x, 10));
  if (isNaN(h)) return hhmm;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m || 0).padStart(2, '0')} ${ampm}`;
};


// ─── Sub-components ────────────────────────────────────────────────────────
const TicketSkeleton = () => (
  <div className="ticket-card animate-pulse" data-testid="ticket-skeleton">
    <div className="ticket-card-top">
      <div className="h-4 w-24 bg-white/10 rounded mb-3" />
      <div className="h-7 w-3/4 bg-white/15 rounded mb-2" />
      <div className="h-4 w-1/2 bg-white/10 rounded" />
    </div>
    <div className="ticket-perforation" />
    <div className="ticket-card-bottom flex flex-col items-center">
      <div className="h-64 w-64 bg-white/10 rounded-xl my-4" />
      <div className="h-4 w-32 bg-white/10 rounded" />
    </div>
  </div>
);

const ErrorState = ({ icon: Icon, title, message, accent = 'red' }) => {
  const accents = {
    red: 'text-red-400',
    amber: 'text-amber-400',
    gray: 'text-zinc-400',
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className="ticket-card-error"
      data-testid="ticket-error"
    >
      <Icon size={56} className={`${accents[accent]} mb-4`} strokeWidth={1.5} />
      <h2 className="text-xl font-semibold text-white mb-2 text-center">{title}</h2>
      <p className="text-sm text-zinc-400 text-center max-w-xs leading-relaxed">{message}</p>
    </motion.div>
  );
};


const TicketCard = ({ ticket }) => {
  const status = deriveStatus(ticket);
  const totalPasses = (ticket.items || []).reduce((sum, i) => sum + (i.quantity || 0), 0);

  // For ALREADY USED / EXPIRED / CANCELLED: dim the QR
  const dimQR = status.label !== 'VALID';

  return (
    <motion.article
      initial={{ opacity: 0, y: 16, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="ticket-card"
      data-testid="ticket-card"
    >
      {/* ── TOP: brand + event header ────────────────────────────── */}
      <header className="ticket-card-top">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-zinc-400">
            <TicketIcon size={14} className="text-[var(--accent-gold)]" />
            Scratch Unleashed
          </div>
          <span
            className={`px-2.5 py-1 rounded-full border text-[10px] font-semibold uppercase tracking-wider ${statusToneClasses[status.tone]}`}
            data-testid="ticket-status-badge"
          >
            {status.label}
          </span>
        </div>

        <h1
          className="text-[1.55rem] sm:text-3xl font-semibold text-white leading-tight tracking-tight"
          data-testid="ticket-event-title"
        >
          {ticket.event_title || 'Event'}
        </h1>

        {/* event meta strip */}
        <div className="mt-4 grid grid-cols-2 gap-y-2.5 gap-x-4 text-sm">
          <div className="flex items-center gap-2 text-zinc-300" data-testid="ticket-event-date">
            <Calendar size={14} className="text-[var(--accent-gold)] shrink-0" />
            <span className="truncate">{formatEventDate(ticket.event_date)}</span>
          </div>
          <div className="flex items-center gap-2 text-zinc-300" data-testid="ticket-event-time">
            <Clock size={14} className="text-[var(--accent-gold)] shrink-0" />
            <span className="truncate">{formatTime(ticket.event_time)}</span>
          </div>
          {ticket.venue ? (
            <div className="flex items-center gap-2 text-zinc-300 col-span-2" data-testid="ticket-venue">
              <MapPin size={14} className="text-[var(--accent-gold)] shrink-0" />
              <span className="truncate">{ticket.venue}</span>
            </div>
          ) : null}
        </div>
      </header>

      {/* ── perforated divider ──────────────────────────────────── */}
      <div className="ticket-perforation" aria-hidden="true">
        <span className="ticket-notch ticket-notch-left" />
        <span className="ticket-notch ticket-notch-right" />
      </div>

      {/* ── BOTTOM: QR + booking code + items ───────────────────── */}
      <section className="ticket-card-bottom">
        {/* large QR — encodes the 7-char branded short-code (NOT the full
            ~200-char JWT) so dots are much larger and easier to scan, exactly
            like the wallet-redemption QR. The admin scanner accepts either
            shape, so the JWT fallback still works for legacy bookings. */}
        <div className={`ticket-qr-wrap ${dimQR ? 'ticket-qr-dim' : ''}`} data-testid="ticket-qr">
          {(ticket.short_code || ticket.ticket_token) ? (
            <QRCodeSVG
              value={ticket.short_code || ticket.ticket_token}
              size={248}
              level="M"
              bgColor="#FFFFFF"
              fgColor="#0A0A0A"
              marginSize={2}
              className="ticket-qr-svg"
            />
          ) : (
            <div className="ticket-qr-placeholder">
              <AlertCircle size={32} className="text-zinc-500" />
            </div>
          )}
          {status.label === 'ALREADY USED' && (
            <div className="ticket-qr-stamp ticket-qr-stamp-gray" data-testid="ticket-stamp-used">USED</div>
          )}
          {(status.label === 'EXPIRED' || status.label === 'CANCELLED' || status.label === 'REFUNDED') && (
            <div className="ticket-qr-stamp ticket-qr-stamp-red" data-testid="ticket-stamp-void">VOID</div>
          )}
        </div>

        <p className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 mt-5">Booking Code</p>
        <p
          className="text-base sm:text-lg font-mono font-semibold text-white tracking-wider mt-1"
          data-testid="ticket-booking-code"
        >
          {ticket.booking_code}
        </p>

        {/* pass breakdown */}
        {(ticket.items || []).length > 0 && (
          <div className="ticket-items" data-testid="ticket-items">
            <p className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-3">
              Passes ({totalPasses})
            </p>
            <ul className="space-y-2">
              {ticket.items.map((it, idx) => (
                <li
                  key={idx}
                  className="flex items-center justify-between text-sm"
                  data-testid={`ticket-item-${idx}`}
                >
                  <span className="text-zinc-200 flex items-center gap-2">
                    {it.is_vip && (
                      <span className="px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider bg-[var(--accent-gold)]/15 text-[var(--accent-gold)] border border-[var(--accent-gold)]/30">
                        VIP
                      </span>
                    )}
                    {it.pass_type_name}
                  </span>
                  <span className="text-zinc-400 font-mono">× {it.quantity}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* validity hint */}
        {ticket.ticket_expires_at && status.label === 'VALID' && (
          <p className="text-[11px] text-zinc-500 mt-5 text-center" data-testid="ticket-validity">
            Valid until {formatEventDate(ticket.ticket_expires_at.slice(0, 10))} · 23:59
          </p>
        )}
        {status.label === 'ALREADY USED' && ticket.checked_in_at && (
          <p className="text-[11px] text-zinc-500 mt-5 text-center" data-testid="ticket-checked-in-at">
            Checked in at {new Date(ticket.checked_in_at).toLocaleString('en-IN')}
          </p>
        )}
      </section>
    </motion.article>
  );
};


// ─── Page ──────────────────────────────────────────────────────────────────
export default function PublicTicketPage() {
  const { token: rawToken } = useParams();
  const [state, setState] = useState({ status: 'loading', data: null, error: null });

  // ── Domain bridge — redirect preview / non-canonical hosts to production ──
  // The Meta-approved WhatsApp template `scratch_pass_conf` was submitted with
  // `https://scratch-dev.preview.emergentagent.com/tickets/{{1}}` baked into
  // its URL button (the button URL cannot be edited without a Meta re-approval,
  // typically 1-24 hrs). Until the template is re-approved with the canonical
  // domain, every confirmation-link tap lands here on the preview host — where
  // the customer's booking token doesn't exist because the booking was written
  // to the production database.
  //
  // We fix that instantly with a client-side 302 to `scratchjsr.com/tickets/<token>`
  // as soon as this component mounts on any non-canonical host. Localhost and
  // 127.* are exempted so local development is unaffected. Add `?stay=1` to
  // bypass the redirect for manual testing.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const host = window.location.hostname;
    const isCanonical = host === 'scratchjsr.com' || host === 'www.scratchjsr.com';
    const isLocal = host === 'localhost' || host.startsWith('127.') || host.endsWith('.local');
    const bypass = new URLSearchParams(window.location.search).get('stay') === '1';
    if (!isCanonical && !isLocal && !bypass && rawToken) {
      // Preserve token + any query string so short-links keep working.
      const target = `https://scratchjsr.com/tickets/${encodeURIComponent(rawToken)}${window.location.search || ''}`;
      window.location.replace(target);
    }
  }, [rawToken]);

  // ── Defensive token sanitization ─────────────────────────────────────
  // The Meta-approved `scratch_pass_conf` template URL was published with a
  // literal `{{4}}` baked into the static URL (`/tickets/{{4}}{{1}}`). Every
  // historical confirmation link therefore arrives as `/tickets/{{4}}<token>`
  // (or the URL-encoded form `/tickets/%7B%7B4%7D%7D<token>`). Strip those
  // prefixes here so old links keep working until the template is corrected
  // at Meta. Also defensively strip any leftover `{{n}}` Meta placeholders.
  const token = (rawToken || '')
    .replace(/^%7B%7B\d+%7D%7D/i, '')   // URL-encoded {{N}} prefix
    .replace(/^\{\{\d+\}\}/, '')         // decoded {{N}} prefix
    .trim();

  useEffect(() => {
    let cancelled = false;
    const fetchTicket = async () => {
      if (!token) {
        setState({ status: 'not_found', data: null, error: null });
        return;
      }
      try {
        const res = await fetch(`${BACKEND_URL}/api/v2/public/tickets/${encodeURIComponent(token)}`, {
          method: 'GET',
          headers: { Accept: 'application/json' },
        });
        if (cancelled) return;
        if (res.status === 404) {
          // ── Fallback: might be a Special Event ticket token (Nazakat & Shararat, etc.) ──
          // The Meta-approved `scratch_pass_conf` template URL always points to
          // /tickets/{{1}}. When {{1}} is a special-event token, redirect the
          // user to the canonical special-event ticket page.
          try {
            const sxeRes = await fetch(
              `${BACKEND_URL}/api/public/special-events/booking/${encodeURIComponent(token)}`,
              { method: 'GET', headers: { Accept: 'application/json' } },
            );
            if (cancelled) return;
            if (sxeRes.ok) {
              const sxeData = await sxeRes.json();
              const slug = sxeData?.event_slug || 'nazakat-shararat';
              setState({
                status: 'redirect_special_event',
                data: { slug, token },
                error: null,
              });
              return;
            }
          } catch (_sxeErr) {
            // Ignore — fall through to not_found
          }
          setState({ status: 'not_found', data: null, error: null });
          return;
        }
        if (res.status === 429) {
          const retry = parseInt(res.headers.get('Retry-After') || '60', 10);
          setState({ status: 'rate_limited', data: null, error: { retry } });
          return;
        }
        if (!res.ok) {
          setState({ status: 'error', data: null, error: { code: res.status } });
          return;
        }
        const data = await res.json();
        if (cancelled) return;
        setState({ status: 'ok', data, error: null });
      } catch (e) {
        if (cancelled) return;
        setState({ status: 'error', data: null, error: { code: 0, message: 'Network error' } });
      }
    };
    fetchTicket();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Immediate hard-redirect when we identify the token as a special-event ticket.
  // Rendered before the AnimatePresence tree so no visual flash of the loader.
  if (state.status === 'redirect_special_event' && state.data?.slug && state.data?.token) {
    return (
      <Navigate
        to={`/events/specialevents/${state.data.slug}/ticket/${state.data.token}`}
        replace
      />
    );
  }

  return (
    <div className="ticket-page" data-testid="ticket-page">
      <div className="ticket-page-bg" aria-hidden="true" />

      <main className="ticket-page-shell">
        <AnimatePresence mode="wait">
          {state.status === 'loading' && (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="w-full flex justify-center"
            >
              <div className="flex flex-col items-center text-zinc-500" data-testid="ticket-loading">
                <Loader2 size={28} className="animate-spin text-[var(--accent-gold)] mb-3" />
                <p className="text-sm">Loading ticket…</p>
              </div>
            </motion.div>
          )}

          {state.status === 'ok' && state.data && (
            <motion.div
              key="ok"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="w-full max-w-md mx-auto"
            >
              <TicketCard ticket={state.data} />
              <p className="ticket-footnote">Show this ticket at venue entry · Single use</p>
            </motion.div>
          )}

          {state.status === 'not_found' && (
            <motion.div
              key="notfound"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="w-full max-w-md mx-auto"
            >
              <ErrorState
                icon={XCircle}
                title="Ticket Not Found"
                message="This ticket link is invalid, expired, or no longer active. If you believe this is a mistake, please contact the venue."
                accent="red"
              />
            </motion.div>
          )}

          {state.status === 'rate_limited' && (
            <motion.div
              key="429"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="w-full max-w-md mx-auto"
            >
              <ErrorState
                icon={Hourglass}
                title="Too Many Requests"
                message={`You're refreshing too quickly. Please try again in ${state.error?.retry || 60} seconds.`}
                accent="amber"
              />
            </motion.div>
          )}

          {state.status === 'error' && (
            <motion.div
              key="error"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="w-full max-w-md mx-auto"
            >
              <ErrorState
                icon={AlertCircle}
                title="Couldn't Load Ticket"
                message="We hit a snag fetching this ticket. Check your connection and try again."
                accent="amber"
              />
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Watermark logo top-right (subtle) */}
      <div className="ticket-page-watermark" aria-hidden="true">
        <CheckCircle2 size={14} className="text-[var(--accent-gold)]/70" />
        <span>Verified Ticket</span>
      </div>
    </div>
  );
}
